import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { initializePaddle, Paddle, PricePreviewParams } from '@paddle/paddle-js';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Plan, TreeSubscriptionInfo } from '../models/plan.model';
import { formatMoney } from '../money';

/**
 * Ceny z katalogu Paddle: identyfikator ceny → kwota w najmniejszej jednostce
 * i waluta. Trzymamy je SUROWO, a nie jako gotowy tekst - format zależy od
 * języka interfejsu, a ten można przełączyć już po wczytaniu cen.
 */
export type PaddlePrices = Record<string, { amount: number; currency: string }>;

/**
 * Abonamenty drzew. Odczyty (cennik, plan + zużycie drzewa) oraz płatności
 * Paddle: backend tworzy transakcję, a frontend otwiera na jej identyfikatorze
 * nakładkę Paddle.js (Paddle nie ma osobnej hostowanej strony płatności).
 * Paddle.js pochodzi z pakietu @paddle/paddle-js (bez ręcznego <script> z CDN).
 */
@Injectable()
export class SubscriptionService {

    /** Po tylu milisekundach przestajemy czekać na ceny i pokazujemy te z bazy. */
    private static readonly priceTimeoutMs = 6000;

    /** Jednorazowa inicjalizacja Paddle.js; null = brak tokenu albo błąd startu. */
    private paddleReady?: Promise<Paddle | null>;

    /** Kupujący przekazany już do Paddle Retain - żeby nie powtarzać Update(). */
    private retainCustomerId?: string;

    constructor(private httpClient: HttpClient) { }

    /** Lista aktywnych planów dla cennika - endpoint publiczny */
    getPlans() {
        return this.httpClient.get<Plan[]>(environment.backendUrl + '/subscription/plans');
    }

    /** Kraj klienta odczytany przez backend z nagłówków proxy/CDN; null = nieznany. */
    getCountry() {
        return this.httpClient.get<{ country: string | null }>(
            environment.backendUrl + '/subscription/country');
    }

    /** Plan efektywny + zużycie + status abonamentu drzewa (administrator drzewa) */
    getTreeSubscription(treeId: string) {
        return this.httpClient.get<TreeSubscriptionInfo>(
            environment.backendUrl + '/subscription/tree/' + treeId);
    }

    /**
     * Utworzenie transakcji Paddle. mode 'subscription' = auto-odnowienie
     * co rok (karta/PayPal), mode 'payment' = jednorazowa opłata za rok
     * bez odnowienia. Zwrócony identyfikator otwiera nakładkę checkoutu.
     * Metod płatności NIE zapowiadamy w interfejsie - Paddle dobiera je
     * do kraju klienta, więc obietnica z przycisku mogłaby się nie spełnić.
     */
    createCheckout(treeId: string, planCode: string, mode: 'subscription' | 'payment') {
        return this.httpClient.post<{ transaction_id: string }>(
            environment.backendUrl + '/subscription/checkout/tree/' + treeId,
            { data: { plan_code: planCode, mode } });
    }

    /** Portal klienta Paddle - faktury, karta, anulowanie, zmiana planu */
    openPortal(treeId: string) {
        return this.httpClient.post<{ url: string }>(
            environment.backendUrl + '/subscription/portal/tree/' + treeId, {});
    }

    /**
     * Otwiera nakładkę Paddle na gotowej transakcji. Po udanej płatności
     * Paddle przekierowuje na successUrl (obsługiwane ?checkout=success).
     * Zwraca false, gdy Paddle.js nie mógł wystartować (np. brak tokenu).
     */
    async openCheckout(transactionId: string, successUrl: string): Promise<boolean> {
        const paddle = await this.loadPaddle();
        if (!paddle) {
            return false;
        }
        paddle.Checkout.open({
            transactionId,
            settings: { successUrl }
        });
        return true;
    }

    /**
     * Wskazuje Paddle Retain zalogowanego kupującego (pwCustomer).
     *
     * Retain (odzyskiwanie płatności, ścieżka rezygnacji) rozpoznaje klienta po
     * IDENTYFIKATORZE PADDLE `ctm_...` - nie po naszym user.id ani po adresie
     * e-mail. Bierzemy go z `GET /subscription/tree/{id}` (kolumna
     * tree_subscription.paddle_customer_id), więc jest znany dopiero po
     * wczytaniu abonamentu drzewa, już po starcie Paddle.js. Dlatego nie
     * wchodzi do `initializePaddle()`, tylko dokładamy go potem przez
     * `Paddle.Update()` - to ta sama opcja, tylko ustawiona później.
     *
     * Drzewo bez zakupu nie ma jeszcze klienta w Paddle; wtedy nie ma czego
     * przekazać i wywołanie nic nie robi.
     */
    async setRetainCustomer(customerId: string | null | undefined): Promise<void> {
        if (!customerId || customerId === this.retainCustomerId) {
            return;
        }
        const paddle = await this.loadPaddle();
        if (!paddle) {
            return;
        }
        paddle.Update({ pwCustomer: { id: customerId } });
        this.retainCustomerId = customerId;
    }

    /**
     * Roczna cena Paddle planu (pri_...): najpierw z tabeli plan (backend),
     * zapasowo z environment.paddlePriceIds. Pusty string = brak ceny.
     */
    planPriceId(plan: Plan): string {
        return plan.paddle_price_id ?? environment.paddlePriceIds[plan.code] ?? '';
    }

    /**
     * Cena planu do pokazania: z katalogu Paddle, a gdy go nie ma - zapas
     * z tabeli plan (backend trzyma go zgodnym z katalogiem przez webhook
     * price.updated). Obie drogi idą przez ten sam formater, więc karta
     * wygląda tak samo niezależnie od tego, skąd wzięła kwotę.
     * Pusty string = plan darmowy albo cena nieznana.
     */
    planPrice(plan: Plan, prices: PaddlePrices, lang: string): string {
        const zPaddle = prices[this.planPriceId(plan)];
        if (zPaddle) {
            return formatMoney(zPaddle.amount, zPaddle.currency, lang);
        }
        if (!plan.price_cents || !plan.price_currency) {
            return '';
        }
        return formatMoney(plan.price_cents, plan.price_currency, lang);
    }

    /**
     * Czy w ogóle jest skąd wziąć ceny z Paddle. Bez tego nie ma po co
     * pokazywać szkieletu - karta od razu bierze cenę zapasową z tabeli plan.
     */
    canLoadPrices(plans: Plan[]): boolean {
        return !!environment.paddleClientToken
            && plans.some((plan) => !!this.planPriceId(plan));
    }

    /**
     * Lokalne ceny roczne dla kart planów: kraj wykrywa backend z nagłówków
     * proxy (bez niego Paddle sam po IP), kwoty formatuje Paddle. Pusty wynik
     * = Paddle niedostępny, pokaż cenę zapasową z tabeli plan.
     *
     * Zawsze się rozstrzyga: gdy Paddle.js nie wystartuje (np. blokada reklam
     * albo brak sieci), inicjalizacja potrafi wisieć bez końca, a karta
     * zostawałaby wtedy na zawsze ze szkieletem zamiast ceny.
     */
    async localizedPlanPrices(plans: Plan[]): Promise<PaddlePrices> {
        const priceIds = [...new Set(plans.map((p) => this.planPriceId(p)).filter((id) => !!id))];
        if (!this.canLoadPrices(plans) || priceIds.length === 0) {
            return {};
        }
        let country: string | null = null;
        try {
            country = (await firstValueFrom(this.getCountry()))?.country ?? null;
        } catch {
            country = null;
        }
        return Promise.race([
            this.previewPrices(priceIds, country),
            new Promise<PaddlePrices>((resolve) =>
                setTimeout(() => resolve({}), SubscriptionService.priceTimeoutMs))
        ]);
    }

    /**
     * Sformatowane ceny katalogu dla kraju kupującego (pri_... -> „20,00 €").
     * Bez kodu kraju Paddle sam wykrywa lokalizację po IP odwiedzającego -
     * wtedy adresu nie przekazujemy wcale. Pusty wynik = Paddle niedostępny.
     *
     * Kwotę formatujemy SAMI (z surowego `totals.total` i waluty odpowiedzi),
     * a nie bierzemy gotowego `formattedTotals.total`: Paddle formatuje po
     * swojemu („€20.00"), a cena zapasowa z bazy szłaby przez Intl („20,00 €").
     * Obie drogi muszą wyglądać tak samo, bo karta potrafi przejść z jednej
     * na drugą w trakcie ładowania.
     */
    async previewPrices(priceIds: string[], country: string | null): Promise<PaddlePrices> {
        const paddle = await this.loadPaddle();
        if (!paddle || priceIds.length === 0) {
            return {};
        }
        const params: PricePreviewParams = {
            items: priceIds.map((priceId) => ({ priceId, quantity: 1 }))
        };
        if (country) {
            params.address = { countryCode: country };
        }
        try {
            const response = await paddle.PricePreview(params);
            const currency = response.data.currencyCode;
            const prices: PaddlePrices = {};
            for (const item of response.data.details.lineItems) {
                const amount = Number(item.totals.total);
                if (Number.isFinite(amount)) {
                    prices[item.price.id] = { amount, currency };
                }
            }
            return prices;
        } catch {
            return {};
        }
    }

    /** Jednorazowa inicjalizacja Paddle.js. */
    private loadPaddle(): Promise<Paddle | null> {
        if (!environment.paddleClientToken) {
            return Promise.resolve(null);
        }
        if (!this.paddleReady) {
            this.paddleReady = initializePaddle({
                token: environment.paddleClientToken,
                environment: environment.paddleEnvironment
            }).then((paddle) => paddle ?? null)
                .catch((error) => {
                    // diagnostyka: dlaczego nakladka/ceny Paddle nie dzialaja
                    console.warn('Paddle.js nie wystartowal', error);
                    return null;
                });
        }
        return this.paddleReady;
    }
}
